import {Inject, Injectable, Optional} from '@angular/core';
import {environment} from "../../../environments/environment";
import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {PermissionService} from "../permission-service/permission.service";
import {Observable} from "rxjs";
import {BASE_PATH, Configuration, Language, Product, Version} from "../api";
import {CustomHttpUrlEncodingCodec} from "../api/encoder";


@Injectable({
    providedIn: 'root'
})
// Service for handling files
export class DownloadService {
    public defaultHeaders = new HttpHeaders();
    public configuration = new Configuration();
    protected basePath = '/';
    private readonly baseUrlArtefact = environment.apiIp + "/api/ArtefactManagement/";
    private readonly baseUrlVersion = environment.apiIp + "/api/Version/";
    private readonly baseUrlProduct = environment.apiIp + "/api/Product/";

    constructor(private permissionService: PermissionService, protected httpClient: HttpClient, @Optional() @Inject(BASE_PATH) basePath: string, @Optional() configuration: Configuration) {
        if (basePath) {
            this.basePath = basePath;
        }
        if (configuration) {
            this.configuration = configuration;
            this.basePath = basePath || configuration.basePath || this.basePath;
        }
    }

    private downloadFile(response: any, fileName: string) {
        let dataType = response.type;
        let binaryData = [];
        binaryData.push(response);
        let downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, {type: dataType}));
        downloadLink.setAttribute('download', fileName);
        document.body.appendChild(downloadLink);
        downloadLink.click();
    }


    public deleteArtefact(versionId: number, observe: any = 'body', reportProgress: boolean = false): Observable<any> {

        if (versionId === null || versionId === undefined) {
            throw new Error('Required parameter versionId was null or undefined when calling apiArtefactVersionsVersionIdDelete.');
        }

        let headers = this.defaultHeaders;

        // to determine the Accept header
        let httpHeaderAccepts: string[] = [
            'text/plain',
            'application/json',
            'text/json'
        ];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        // to determine the Content-Type header
        const consumes: string[] = [];

        return this.httpClient.request<any>('delete', `${this.basePath}/api/ArtefactManagement/Versions/${encodeURIComponent(String(versionId))}`,
            {
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    async downloadArtefact(version: Version): Promise<void> {
        const route = "DownloadVersionArtifact/" + version.id;
        const url = this.baseUrlArtefact + route;
        const headers = await this.getAuthHeader();
        const fileName = version.artefact ? version.artefact : "FileName";

        this.httpClient.get(url, {headers, responseType: 'blob' as 'json'}).subscribe(
            (response: any) => {
                this.downloadFile(response, fileName);
            }
        )
    }

    public async uploadArtefact(file?: File, versionId?: number, reportProgress?: boolean) {
        let queryParameters = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});

        let headers = await this.getAuthHeader();

        // to determine the Content-Type header
        const consumes: string[] = [
            'multipart/form-data'
        ];

        const canConsumeForm = this.canConsumeForm(consumes);

        let formParams: { append(param: string, value: any): void; };
        let useForm = false;
        let convertFormParamsToString = false;
        useForm = canConsumeForm;
        if (useForm) {
            formParams = new FormData();
            if (file) {
                formParams.append(file.name, file)
            }
        } else {
            formParams = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});
        }

        if (file !== undefined) {
            formParams = formParams.append('file', <any>file) as any || formParams;
        }

        return this.httpClient.request<any>('post', `${environment.apiIp}/api/ArtefactManagement/UploadArtefact/` + versionId,
            {
                body: convertFormParamsToString ? formParams.toString() : formParams,
                params: queryParameters,
                //withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: "response",
                reportProgress: reportProgress
            }
        );
    }

    async downloadVersionTranslationXml(version: Version, language: Language): Promise<void> {
        const route = `GetExportFile?versionId=${version.id}&langId=${language.id}`;
        const url = this.baseUrlVersion + route;
        const headers = await this.getAuthHeader();
        const fileName: string = `RIS-Export-Version_${version?.name}-Language_${language.languageCode}.xml`;

        this.httpClient.get(url, {headers, responseType: 'blob' as 'json'}).subscribe(
            (response: any) => {
                this.downloadFile(response, fileName);
            }
        )
    }

    async downloadProductTranslationXml(product: Product, language: Language): Promise<void> {
        const route = `GetExportFile?productId=${product.id}&langId=${language.id}`;
        const url = this.baseUrlProduct + route;
        const headers = await this.getAuthHeader();
        const fileName: string = `RIS-Export-Product_${product?.name}-Language_${language.languageCode}.xml`;

        this.httpClient.get(url, {headers, responseType: 'blob' as 'json'}).subscribe(
            (response: any) => {
                this.downloadFile(response, fileName);
            }
        )
    }


    public async uploadProductIcon(file?: File, productId?: number, reportProgress?: boolean) {
        let queryParameters = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});

        let headers = await this.getAuthHeader();

        // to determine the Content-Type header
        const consumes: string[] = [
            'multipart/form-data'
        ];

        const canConsumeForm = this.canConsumeForm(consumes);

        let formParams: { append(param: string, value: any): void; };
        let useForm = false;
        let convertFormParamsToString = false;
        useForm = canConsumeForm;
        if (useForm) {
            formParams = new FormData();
            if (file) {
                formParams.append(file.name, file)
            }
        } else {
            formParams = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});
        }

        if (file !== undefined) {
            formParams = formParams.append('file', <any>file) as any || formParams;
        }

        return this.httpClient.request<any>('post', `${environment.apiIp}/api/Product/${productId}/UpdateProductIcon`,
            {
                body: convertFormParamsToString ? formParams.toString() : formParams,
                params: queryParameters,
                //withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: "response",
                reportProgress: reportProgress
            }
        );
    }

    /**
     * @param consumes string[] mime-types
     * @return true: consumes contains 'multipart/form-data', false: otherwise
     */
    private canConsumeForm(consumes: string[]): boolean {
        const form = 'multipart/form-data';
        for (const consume of consumes) {
            if (form === consume) {
                return true;
            }
        }
        return false;
    }

    private async getAuthHeader(): Promise<HttpHeaders> {
        const token = await this.permissionService.getAccessToken();
        return new HttpHeaders().set('authorization', 'Bearer ' + token);
    }


}
